import { useMemo, useState } from "react";
import type { FxRatePoint, FxRateStatus } from "../../api/kurssicherung.ts";

export interface KursentwicklungProps {
  rates: readonly FxRatePoint[];
  rateStatus: FxRateStatus;
}

const monate = [1, 3, 6, 12] as const;
const zahl = (wert: string) => Number(wert);
const kurs = (wert: string) => zahl(wert).toLocaleString("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const kalendertag = (wert: string) => {
  const [jahr, monat, tag] = wert.split("-");
  return `${tag}.${monat}.${jahr}`;
};
const abrufzeit = (wert: string) => new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
}).format(new Date(wert));
function monateZurueck(letzter: string, monate: number): Date {
  const datum = new Date(`${letzter}T00:00:00Z`);
  const tag = datum.getUTCDate();
  datum.setUTCDate(1);
  datum.setUTCMonth(datum.getUTCMonth() - monate);
  const letzterTag = new Date(Date.UTC(datum.getUTCFullYear(), datum.getUTCMonth() + 1, 0)).getUTCDate();
  datum.setUTCDate(Math.min(tag, letzterTag));
  return datum;
}

export function Kursentwicklung({ rates, rateStatus }: KursentwicklungProps) {
  const [zeitraum, setZeitraum] = useState<(typeof monate)[number]>(12);
  const sichtbareRaten = useMemo(() => {
    const sortiert = [...rates].sort((a, b) => a.rateDate.localeCompare(b.rateDate));
    const letzte = sortiert.at(-1)?.rateDate;
    if (!letzte) return [];
    const grenze = monateZurueck(letzte, zeitraum);
    return sortiert.filter((rate) => new Date(`${rate.rateDate}T00:00:00Z`) >= grenze);
  }, [rates, zeitraum]);
  const werte = sichtbareRaten.map((rate) => zahl(rate.eurUsdRate));
  const min = werte.length ? Math.min(...werte) : null;
  const max = werte.length ? Math.max(...werte) : null;
  const aktuell = sichtbareRaten.at(-1) ?? null;
  const vorheriger = sichtbareRaten.at(-2) ?? null;
  const veraenderung = aktuell && vorheriger ? zahl(aktuell.eurUsdRate) - zahl(vorheriger.eurUsdRate) : null;
  const tageshoch = aktuell ? Math.max(...rates.filter((rate) => rate.rateDate === aktuell.rateDate).map((rate) => zahl(rate.eurUsdRate))) : null;

  return (
    <section className="fx-overview-card fx-overview-rates" aria-labelledby="fx-rates-title">
      <div className="fx-overview-card-head"><div><span className="fx-overview-eyebrow">EZB</span><h2 id="fx-rates-title">EUR / USD</h2></div><strong className={rateStatus.state === "stale" ? "fx-overview-stale" : "fx-overview-fresh"}>{rateStatus.state === "stale" ? "EZB-Kurs veraltet" : "EZB-Kurs frisch"}</strong></div>
      <div className="fx-overview-periods" aria-label="Zeitraum">{monate.map((monat) => <button type="button" key={monat} className={zeitraum === monat ? "active" : ""} onClick={() => setZeitraum(monat)}>{monat} Monat{monat === 1 ? "" : "e"}</button>)}</div>
      {aktuell ? <div className="fx-overview-rate-current"><span>EZB-Referenzkurs · {kalendertag(aktuell.rateDate)}<br />Abruf {abrufzeit(aktuell.fetchedAt)}</span><strong>{kurs(aktuell.eurUsdRate)}</strong></div> : <p className="empty-note">Noch keine EZB-Kurse importiert.</p>}
      {sichtbareRaten.length ? <><div className="fx-overview-chart" aria-label="EZB-Kursverlauf"><div className="fx-overview-y-axis"><span>{kurs(String(max))}</span><span>{kurs(String(min))}</span></div><div className="fx-overview-plot"><svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="Linienverlauf">{sichtbareRaten.length > 1 && <polyline fill="none" points={sichtbareRaten.map((rate, index) => `${(index / (sichtbareRaten.length - 1)) * 100},${90 - ((zahl(rate.eurUsdRate) - (min ?? 0)) / Math.max((max ?? 0) - (min ?? 0), .0001)) * 80}`).join(" ")} />}</svg><div className="fx-overview-x-axis">{sichtbareRaten.map((rate) => <span data-testid="fx-rate-point" key={rate.id}>{kalendertag(rate.rateDate)}</span>)}</div></div></div><div className="fx-overview-rate-stats"><span>Veränderung <strong>{veraenderung === null ? "—" : `${veraenderung >= 0 ? "+" : ""}${kurs(String(veraenderung))}`}</strong></span><span>Tief <strong>{sichtbareRaten.length < 2 ? "—" : kurs(String(min))}</strong></span><span>Hoch <strong>{sichtbareRaten.length < 2 ? "—" : kurs(String(max))}</strong></span><span>Tageshoch <strong>{tageshoch === null ? "—" : kurs(String(tageshoch))}</strong></span></div></> : null}
    </section>
  );
}
